import { Outlet, useLocation, matchPath } from "react-router-dom";

import "./ecommerce-theme.css";

import { TemplateProvider, useTemplate } from "../../context/TemplateContext";
import { CartProvider } from "../../context/CartContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";

/**
 * Outer layout for every SRC-styled ecommerce template route. Applied to
 * both the industry-keyed showroom (`/template/:industryKey/*`) and real
 * published stores (`/site/:slug/*` when `industry_template === 'ecommerce'`).
 *
 * Provides:
 *   - TemplateProvider   — resolves `config` from URL params OR explicit props
 *   - CartProvider       — wired to the resolved preset's pricing
 *   - Header / Footer    — SRC-styled chrome shared by all 5 template pages
 *   - CartDrawer         — SRC-styled slide-in cart with free-ship progress
 *
 * The `.strvkt-theme` class scopes the SRC fonts (Archivo Black / Space Grotesk /
 * DM Sans) + effects (marquee, grain, link-underline) to ecommerce only.
 */
function TemplateLayoutInner({ homeMatchers }) {
  const { config } = useTemplate();
  const { pathname } = useLocation();

  // "Home" for header-transparency purposes = pathname exactly matches
  // one of the mount points ("/" for showroom, "/site/:slug" for real).
  const isHome = homeMatchers.some((p) => matchPath({ path: p, end: true }, pathname));

  return (
    <CartProvider pricing={config.pricing}>
      <div className="strvkt-theme min-h-screen flex flex-col">
        <Header
          config={config}
          basePath={homeMatchers[0].replace(/\/$/, "") === "/" ? "" : homeMatchers[0].replace(/\/$/, "")}
          transparent={isHome}
        />
        {/* Home pushes the hero all the way up (behind the transparent header).
            Other pages need padding so content isn't obscured by the fixed header. */}
        <main className={`flex-1 ${isHome ? "" : "pt-[104px]"}`}>
          <Outlet />
        </main>
        <Footer
          config={config}
          basePath={homeMatchers[0].replace(/\/$/, "") === "/" ? "" : homeMatchers[0].replace(/\/$/, "")}
        />
        <CartDrawer
          config={config}
          basePath={homeMatchers[0].replace(/\/$/, "") === "/" ? "" : homeMatchers[0].replace(/\/$/, "")}
        />
      </div>
    </CartProvider>
  );
}

export default function TemplateLayout({ industryKey, overrides, basePath, slug, checkoutEnabled }) {
  // homeMatchers: routes where the header should render transparent+overlaid.
  // Includes the trailing-slash variant for URLs like `/site/<slug>/`.
  const homeMatchers = basePath
    ? [basePath, `${basePath}/`]
    : ["/template/:industryKey", "/template/:industryKey/"];
  return (
    <TemplateProvider industryKey={industryKey} overrides={overrides} basePath={basePath} slug={slug} checkoutEnabled={checkoutEnabled}>
      <TemplateLayoutInner homeMatchers={homeMatchers} />
    </TemplateProvider>
  );
}
